// LedgerEntryForm — admin-only inline form for posting a ledger entry
// against a specific merchant.
//
// Backend gates writes at the route layer (RequireRole(admin) on
// POST /v1/merchants/:id/ledger-entries); the form is still hidden from
// non-admins at the call site to avoid the dead-end "submit → 403" UX.
//
// Rupee-decimal input is normalised to int paise before sending. The
// validation here mirrors what the backend would reject anyway:
//   - amount must be a non-zero finite number
//   - opening must be positive
//   - topup must be positive (use 'adjustment' for debits)
//   - fee is entered as the amount to CHARGE and stored negative. The field
//     says so, and the sign is taken off whatever is typed: 50 and -50 both
//     bill ₹50. Nobody types a fee meaning "credit them", so there is no
//     reading of the input this could get wrong — and the alternative,
//     rejecting a positive number, made the operator do sign arithmetic on
//     every charge to satisfy a storage detail.
//   - adjustment may be signed (negative = debit)
// Backend's CHECK constraint and handler raise 400 ledger_kind_invalid /
// ledger_amount_zero on its own validation; the local checks are UX-only.
import { useState, type FormEvent } from "react";

import { apiErrorMessage } from "@/utils/apiError";
import {
  createLedgerEntry,
  LEDGER_KIND_LABEL,
  OPERATOR_LEDGER_KINDS,
  type LedgerEntryKind,
} from "@/api/merchantLedger";

interface LedgerEntryFormProps {
  merchantId: number;
  onCreated: () => void;
}

export function LedgerEntryForm({
  merchantId,
  onCreated,
}: LedgerEntryFormProps) {
  const [kind, setKind] = useState<LedgerEntryKind>("opening");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setKind("opening");
    setAmount("");
    setNote("");
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = amount.trim();
    if (trimmed === "") {
      setError("Amount is required.");
      return;
    }
    const rupees = Number(trimmed);
    if (!Number.isFinite(rupees) || rupees === 0) {
      setError("Amount must be a non-zero number (rupees; decimals OK).");
      return;
    }
    let amountMinor = Math.round(rupees * 100);
    // A fee is always a charge. Store the negative of whatever magnitude was
    // typed, so a stray minus sign cannot turn a bill into a credit in a
    // table that can only be corrected by posting a second entry.
    if (kind === "fee") {
      amountMinor = -Math.abs(amountMinor);
    }
    if (kind === "opening" && amountMinor < 0) {
      setError("Opening balance must be positive.");
      return;
    }
    if (kind === "topup" && amountMinor <= 0) {
      setError("Top-up must be positive. Use 'adjustment' for debits.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createLedgerEntry(merchantId, {
        kind,
        amount_minor: amountMinor,
        note: note.trim() || undefined,
      });
      reset();
      onCreated();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save the ledger entry."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        padding: "12px 16px",
        borderBottom: "1px solid var(--hair)",
        background: "var(--bg-sunk)",
      }}
    >
      <div className="row gap-8" style={{ alignItems: "flex-end" }}>
        <div className="field" style={{ marginBottom: 0, minWidth: 140 }}>
          <label className="label" htmlFor="ledger-kind">
            Kind
          </label>
          <select
            id="ledger-kind"
            className="input"
            value={kind}
            onChange={(e) => setKind(e.target.value as LedgerEntryKind)}
          >
            {/* Operator kinds only. payout/refund are system-written with an
                idempotency ref; a hand-typed one would back no real payment
                in a table that cannot be corrected by deletion. */}
            {OPERATOR_LEDGER_KINDS.map((k) => (
              <option key={k} value={k}>
                {LEDGER_KIND_LABEL[k]}
                {k === "adjustment" ? " (signed)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, width: 160 }}>
          <label className="label" htmlFor="ledger-amount">
            {kind === "fee" ? "Amount to charge (INR)" : "Amount (INR)"}
          </label>
          <input
            id="ledger-amount"
            className="input"
            type="text"
            inputMode="decimal"
            placeholder="1000.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            aria-describedby={kind === "fee" ? "ledger-amount-hint" : undefined}
          />
          {kind === "fee" && (
            <div
              id="ledger-amount-hint"
              className="dim"
              style={{ fontSize: 11 }}
            >
              Debited from the merchant.
            </div>
          )}
        </div>
        <div className="field" style={{ marginBottom: 0, flex: 1 }}>
          <label className="label" htmlFor="ledger-note">
            Note (optional)
          </label>
          <input
            id="ledger-note"
            className="input"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <button
          className="btn accent"
          type="submit"
          disabled={submitting || amount.trim() === ""}
        >
          {submitting ? "Saving…" : "Add entry"}
        </button>
      </div>
      {error && (
        <div
          style={{ color: "var(--warn, #b42318)", fontSize: 12, marginTop: 8 }}
        >
          {error}
        </div>
      )}
      <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
        Adjustments accept negative amounts for debits (e.g. <code>-500</code>{" "}
        to remove ₹500). Once written, entries are immutable.
      </div>
    </form>
  );
}
